import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RydDial } from './RydDial';

export type ADSR = {
    attack: number;   // seconds
    decay: number;    // seconds
    sustain: number;  // 0..1 level
    release: number;  // seconds
};

export interface RydADSRInputProps {
    value: ADSR;
    onChange: (next: ADSR) => void;
    width?: number;
    height?: number;
    maxTime?: number;          // max seconds for any segment except sustain level
    sustainVisualDuration?: number; // seconds used just for drawing sustain length
    dialSize?: number;
    readOnly?: boolean;
    className?: string;
    style?: React.CSSProperties;
    // Optional: label rendering override
    labels?: {
        attack?: string;
        decay?: string;
        sustain?: string;
        release?: string;
    };
}

/**
 * Interactive ADSR envelope editor with:
 * - Four RydDial controls
 * - Draggable SVG envelope (attack peak, decay end, sustain level, release end)
 * Attack/Decay/Release are time based handles (horizontal drag).
 * Sustain is a level handle (vertical drag).
 *
 * Assumptions:
 * - RydDial component exists and accepts props: value, min, max, step, onChange, label, disabled, size
 */
export const RydADSRInput: React.FC<RydADSRInputProps> = ({
    value,
    onChange,
    width = 380,
    height = 180,
    maxTime = 8,
    sustainVisualDuration = 1.2,
    dialSize = 60,
    readOnly = false,
    className,
    style,
    labels = {}
}) => {
    const { attack, decay, sustain, release } = value;
    const padding = 12;
    const graphHeight = height - 90; // leave room for dials
    const graphWidth = width - padding * 2;

    // Pre-clamp incoming value defensively
    const safeValue: ADSR = {
        attack: clamp(attack, 0.001, maxTime),
        decay: clamp(decay, 0.001, maxTime),
        sustain: clamp(sustain, 0, 1),
        release: clamp(release, 0.001, maxTime)
    };

    // Total "display" time
    const totalDisplayTime =
        safeValue.attack + safeValue.decay + sustainVisualDuration + safeValue.release;

    const scaleX = (t: number) => (t / totalDisplayTime) * graphWidth + padding;
    const scaleY = (level: number) =>
        padding + (1 - level) * (graphHeight - padding * 2);

    // Handle types
    type HandleKey = 'attack' | 'decay' | 'sustain' | 'release';
    interface DragState {
        handle: HandleKey;
        originValue: ADSR;
        startX: number;
        startY: number;
    }
    const dragRef = useRef<DragState | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);

    // Compute handle positions (in data space)
    const handlePositions = useMemo(() => {
        const tA = safeValue.attack;
        const tD = tA + safeValue.decay;
        const tSStart = tD;
        const tSEnd = tD + sustainVisualDuration;
        const tRStart = tSEnd;
        const tREnd = tRStart + safeValue.release;
        return {
            attack: { t: tA, level: 1 },
            decay: { t: tD, level: safeValue.sustain },
            sustain: { t: (tSStart + tSEnd) / 2, level: safeValue.sustain },
            release: { t: tREnd, level: 0 },
            pointsForPath: {
                t0: 0,
                peak: { t: tA, level: 1 },
                decayEnd: { t: tD, level: safeValue.sustain },
                sustainStart: { t: tSStart, level: safeValue.sustain },
                sustainEnd: { t: tSEnd, level: safeValue.sustain },
                releaseEnd: { t: tREnd, level: 0 }
            }
        };
    }, [safeValue, sustainVisualDuration, totalDisplayTime]);

    const pathD = useMemo(() => {
        const p = handlePositions.pointsForPath;
        // Build polyline-like path
        return [
            'M', scaleX(0), scaleY(0),
            'L', scaleX(p.peak.t), scaleY(p.peak.level),
            'L', scaleX(p.decayEnd.t), scaleY(p.decayEnd.level),
            'L', scaleX(p.sustainEnd.t), scaleY(p.sustainEnd.level),
            'L', scaleX(p.releaseEnd.t), scaleY(p.releaseEnd.level)
        ].join(' ');
    }, [handlePositions, scaleX, scaleY]);

    // Hit detection radius in pixels
    const HANDLE_RADIUS = 8;
    const INTERACTIVE_PADDING = 14;

    const getHandleScreenPosition = useCallback((key: HandleKey) => {
        const h = handlePositions[key];
        return {
            x: scaleX(h.t),
            y: scaleY(h.level)
        };
    }, [handlePositions, scaleX, scaleY]);

    const findNearestHandle = (x: number, y: number): HandleKey | null => {
        const keys: HandleKey[] = ['attack', 'decay', 'sustain', 'release'];
        let best: { k: HandleKey; dist: number } | null = null;
        for (const k of keys) {
            const { x: hx, y: hy } = getHandleScreenPosition(k);
            const dx = hx - x;
            const dy = hy - y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d <= HANDLE_RADIUS + INTERACTIVE_PADDING) {
                if (!best || d < best.dist) best = { k, dist: d };
            }
        }
        return best?.k || null;
    };

    const updateFromDrag = (clientX: number, clientY: number) => {
        const ds = dragRef.current;
        if (!ds) return;
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const px = clamp(x - padding, 0, graphWidth);
        const t = (px / graphWidth) * totalDisplayTime;

        const next = { ...ds.originValue };

        switch (ds.handle) {
            case 'attack': {
                const newAttack = clamp(t, 0.001, maxTime);
                // Ensure decay still valid
                next.attack = newAttack;
                // Optionally clamp if overlap
                if (next.decay < 0.001) next.decay = 0.001;
                break;
            }
            case 'decay': {
                // decay handle time = attack + decay
                const newDecay = clamp(t - next.attack, 0.001, maxTime);
                next.decay = newDecay;
                break;
            }
            case 'release': {
                // release handle time = attack + decay + sustainVisualDuration + release
                const newRelease = clamp(
                    t - (next.attack + next.decay + sustainVisualDuration),
                    0.001,
                    maxTime
                );
                next.release = newRelease;
                break;
            }
            case 'sustain': {
                // vertical drag changes sustain level
                const level = 1 - (y - padding) / (graphHeight - padding * 2);
                next.sustain = clamp(level, 0, 1);
                break;
            }
        }
        onChange(next);
    };

    const onPointerDown = (e: React.PointerEvent) => {
        if (readOnly) return;
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const handle = findNearestHandle(x, y);
        if (!handle) return;
        dragRef.current = {
            handle,
            originValue: { ...safeValue },
            startX: x,
            startY: y
        };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!dragRef.current) return;
        updateFromDrag(e.clientX, e.clientY);
    };

    const endDrag = (e: React.PointerEvent) => {
        if (dragRef.current) {
            dragRef.current = null;
        }
        try {
            (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        } catch { }
    };

    // Keyboard adjustments for accessibility
    const onKeyDownHandle = (handle: HandleKey, e: React.KeyboardEvent) => {
        if (readOnly) return;
        const stepTime = 0.01;
        const stepLevel = 0.01;
        const next = { ...safeValue };
        let changed = false;
        if (handle === 'sustain') {
            if (e.key === 'ArrowUp') {
                next.sustain = clamp(next.sustain + stepLevel, 0, 1);
                changed = true;
            } else if (e.key === 'ArrowDown') {
                next.sustain = clamp(next.sustain - stepLevel, 0, 1);
                changed = true;
            }
        } else {
            const inc = e.key === 'ArrowRight';
            const dec = e.key === 'ArrowLeft';
            if (inc || dec) {
                const delta = (inc ? 1 : -1) * stepTime;
                if (handle === 'attack') {
                    next.attack = clamp(next.attack + delta, 0.001, maxTime);
                    changed = true;
                } else if (handle === 'decay') {
                    next.decay = clamp(next.decay + delta, 0.001, maxTime);
                    changed = true;
                } else if (handle === 'release') {
                    next.release = clamp(next.release + delta, 0.001, maxTime);
                    changed = true;
                }
            }
        }
        if (changed) {
            e.preventDefault();
            onChange(next);
        }
    };

    // Hover / focus state
    const [hoverHandle, setHoverHandle] = useState<HandleKey | null>(null);

    // // Provide RydDial placeholder type
    // // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // const RydDial: React.FC<any> = (props) => {
    //     // Replace with actual implementation imported from your library
    //     const { value, min, max, step, onChange, label, disabled, size } = props;
    //     return (
    //         <div style={{ width: size, textAlign: 'center', fontSize: 12, userSelect: 'none', opacity: disabled ? 0.6 : 1 }}>
    //             <div style={{
    //                 border: '1px solid #555',
    //                     borderRadius: '50%',
    //                     width: size,
    //                     height: size,
    //                     display: 'flex',
    //                     alignItems: 'center',
    //                     justifyContent: 'center',
    //                     background: '#1e1e1e',
    //                     marginBottom: 4
    //             }}>
    //                 <input
    //                     type="range"
    //                     min={min}
    //                     max={max}
    //                     step={step}
    //                     disabled={disabled}
    //                     value={value}
    //                     onChange={(e) => onChange(parseFloat(e.target.value))}
    //                     style={{ width: '80%' }}
    //                 />
    //             </div>
    //             <div>{label}</div>
    //             <div style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(value)}</div>
    //         </div>
    //     );
    // };

    const dialCommon = {
        disabled: readOnly,
        size: dialSize
    };

    return (
        <div
            className={className}
            style={{
                width,
                height,
                fontFamily: 'sans-serif',
                display: 'flex',
                flexDirection: 'column',
                ...style
            }}
        >
            <div style={{ flex: 1, position: 'relative' }}>
                <svg
                    ref={svgRef}
                    width={width}
                    height={graphHeight}
                    style={{
                        touchAction: 'none',
                        cursor: dragRef.current ? 'grabbing' : 'default',
                        userSelect: 'none',
                        background: 'linear-gradient(180deg,#141414,#121212)'
                    }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerLeave={endDrag}
                >
                    {/* Background grid */}
                    <GridLines
                        width={width}
                        height={graphHeight}
                        padding={padding}
                        rows={4}
                        cols={6}
                    />

                    {/* Envelope path */}
                    <path
                        d={pathD}
                        stroke="#4fc1ff"
                        strokeWidth={2}
                        fill="none"
                        vectorEffect="non-scaling-stroke"
                    />
                    {/* Area under curve (approx) */}
                    <path
                        d={pathD + ' L ' + scaleX(0) + ' ' + scaleY(0) + ' Z'}
                        fill="url(#adsrFill)"
                        opacity={0.25}
                    />
                    <defs>
                        <linearGradient id="adsrFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4fc1ff" stopOpacity={0.6} />
                            <stop offset="100%" stopColor="#4fc1ff" stopOpacity={0} />
                        </linearGradient>
                    </defs>

                    {/* Handles */}
                    {(['attack', 'decay', 'sustain', 'release'] as HandleKey[]).map((h) => {
                        const { x, y } = getHandleScreenPosition(h);
                        const isActive = dragRef.current?.handle === h;
                        const isHover = hoverHandle === h;
                        return (
                            <g
                                key={h}
                                tabIndex={0}
                                role="slider"
                                aria-label={h + ' handle'}
                                onFocus={() => setHoverHandle(h)}
                                onBlur={() => setHoverHandle((cur) => (cur === h ? null : cur))}
                                onMouseEnter={() => setHoverHandle(h)}
                                onMouseLeave={() => setHoverHandle((cur) => (cur === h ? null : cur))}
                                onKeyDown={(e) => onKeyDownHandle(h, e)}
                            >
                                <circle
                                    cx={x}
                                    cy={y}
                                    r={HANDLE_RADIUS + (isHover || isActive ? 4 : 0)}
                                    fill="transparent"
                                />
                                <circle
                                    cx={x}
                                    cy={y}
                                    r={HANDLE_RADIUS}
                                    fill={isActive ? '#ffffff' : '#4fc1ff'}
                                    stroke={isHover || isActive ? '#fff' : '#1b3f4f'}
                                    strokeWidth={isActive ? 2 : 1}
                                    style={{ transition: 'fill 0.15s, stroke 0.15s' }}
                                />
                            </g>
                        );
                    })}
                </svg>
                <div
                    style={{
                        position: 'absolute',
                        top: 4,
                        right: 8,
                        fontSize: 10,
                        opacity: 0.7,
                        color: 'var(--adsr-legend, #bbb)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <span>A: {value.attack.toFixed(3)}s</span>
                    <span>D: {value.decay.toFixed(3)}s</span>
                    <span>S: {value.sustain.toFixed(3)}</span>
                    <span>R: {value.release.toFixed(3)}s</span>
                </div>

            </div>

            <div
                style={{
                    display: 'flex',
                    // flexGrow: 4,
                    flexDirection: 'row',
                    gap: 12,
                    justifyContent: 'space-between',
                    padding: '4px 8px'
                }}
            >
                <div>
                    <RydDial
                        // label={labels.attack || 'Attack'}
                        min={0.001}
                        max={maxTime}
                        // step={0.001}
                        initialValue={safeValue.attack}
                        update={(v: number) => onChange({ ...safeValue, attack: v })}
                        {...dialCommon}
                    />
                    <span>{labels.attack || 'Attack'}</span>
                </div>
                <div>
                    <RydDial
                        // label={labels.decay || 'Decay'}
                        min={0.001}
                        max={maxTime}
                        // step={0.001}
                        initialValue={safeValue.decay}
                        update={(v: number) => onChange({ ...safeValue, decay: v })}
                        {...dialCommon}
                    />
                    <span>{labels.decay || 'Decay'}</span>
                </div>

                <div>
                    <RydDial
                        // label={labels.sustain || 'Sustain'}
                        min={0}
                        max={1}
                        // step={0.001}
                        initialValue={safeValue.sustain}
                        update={(v: number) => onChange({ ...safeValue, sustain: v })}
                        {...dialCommon}
                    />
                    <span>{labels.sustain || 'Sustain'}</span>
                </div>
                <div>
                    <RydDial
                        // label={labels.release || 'Release'}
                        min={0.001}
                        max={maxTime}
                        // step={0.001}
                        initialValue={safeValue.release}
                        update={(v: number) => onChange({ ...safeValue, release: v })}
                        {...dialCommon}
                    />
                    <span>{labels.release || 'Release'}</span>
                </div>
            </div>
        </div>
    );
};

// Utility grid component
const GridLines: React.FC<{
    width: number;
    height: number;
    padding: number;
    rows: number;
    cols: number;
}> = ({ width, height, padding, rows, cols }) => {
    const lines: JSX.Element[] = [];
    for (let r = 0; r <= rows; r++) {
        const y = padding + ((height - padding * 2) * r) / rows;
        lines.push(
            <line
                key={'row-' + r}
                x1={padding}
                x2={width - padding}
                y1={y}
                y2={y}
                stroke="#2a2a2a"
                strokeWidth={1}
                strokeDasharray="4 4"
            />
        );
    }
    for (let c = 0; c <= cols; c++) {
        const x = padding + ((width - padding * 2) * c) / cols;
        lines.push(
            <line
                key={'col-' + c}
                y1={padding}
                y2={height - padding}
                x1={x}
                x2={x}
                stroke="#2a2a2a"
                strokeWidth={1}
                strokeDasharray="4 4"
            />
        );
    }
    return <g>{lines}</g>;
};

function clamp(v: number, min: number, max: number) {
    return v < min ? min : v > max ? max : v;
}

function formatNumber(n: number) {
    if (n >= 1) return n.toFixed(2);
    if (n >= 0.01) return n.toFixed(3);
    return n.toPrecision(2);
}

export default RydADSRInput;